import { AxiosError, AxiosResponse } from 'axios'
import {
  DefaultNowType,
  LoadingTypes,
  errorLogger,
  getErrorMessages,
  startLoading,
  stopLoading,
  updateAppAlert,
} from '@labrav/common'
import { isEmpty } from 'lodash'
import { AnyAction } from 'redux'

import { getPreSignedUrl, postToSignedUrl } from '../../api/marketplace/index'
import { flyoutS3Url } from '../../common/utils/constants'
import { UploadToS3Unsuccessful } from '../../common/utils/messagesContants'
import { AppDispatch, RootState } from '../../store/types'
import { addEulaFiles, updateOnReview } from '../freeTrials/action'
import { FileUploadType } from '../freeTrials/types'
import {
  addEulaFilesOnPrivateOffer,
  sendPrivateOffer,
} from '../privateOffer/privateOfferCreation/action'
import { addFilesOnResaleAuthorization } from '../resaleAuthorization/action'

import {
  getFileNameFromDate,
  getFileNameFromDateV2,
} from '../../common/utils/getFileNameFromDate'

export type uploadFileProps = {
  partnerId: string
  productId: string
  filesToBeUploaded: File[]
  assetType: string
  folder: string
  defaultNow: DefaultNowType
  saveAsDraft?: boolean
  crmOpportunityId?: string
  crmObjectLink?: string
  fileUploadCallback?: (uplodedFiles: string[]) => void
  bucketName?: string
  showRealFileName?: boolean
}

export const uploadFilesProcessing = ({
  defaultNow,
  assetType,
  partnerId = '',
  folder,
  filesToBeUploaded = [],
  showRealFileName = false,
}: Partial<uploadFileProps>) => {
  const folderPath = `${assetType}/${partnerId}/${folder}`
  const filePathObjects: Record<string, File> = {}
  let path = ''
  const fileNames: string[] = []
  filesToBeUploaded.forEach((file, index) => {
    const dateString = defaultNow ? defaultNow() : ''
    const fileSplit = file.name.split('.')
    const fileType = fileSplit[fileSplit.length - 1]
    const fileName = showRealFileName
      ? getFileNameFromDateV2(dateString, file.name)
      : getFileNameFromDate(dateString, String(index), fileType)
    const finalFileName = `${fileName}`
    fileNames.push(finalFileName)
    filePathObjects[`${folderPath}/${finalFileName}`] = file
    if (isEmpty(path)) {
      path = path.concat(`${folderPath}/${finalFileName}`)
    } else {
      path = path.concat(`,${folderPath}/${finalFileName}`)
    }
  })
  return { path, filePathObjects }
}

export const uploadFiles =
  ({
    partnerId,
    productId,
    filesToBeUploaded,
    assetType,
    folder,
    defaultNow,
    saveAsDraft = false,
    crmOpportunityId,
    crmObjectLink,
    fileUploadCallback,
    bucketName,
    showRealFileName,
  }: uploadFileProps) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const { path, filePathObjects } = await uploadFilesProcessing({
        partnerId,
        filesToBeUploaded,
        folder,
        assetType,
        defaultNow,
        showRealFileName,
      })
      const { data } = await getPreSignedUrl(partnerId, path, bucketName)
      const signedUrlObjects = data[0].presigned_url_data
      const postToS3Promises = Object.keys(signedUrlObjects).map(
        (key: string) => {
          const presignedUrl = signedUrlObjects[key].url
          const fields = signedUrlObjects[key].fields
          const formData = new FormData()
          Object.keys(fields).forEach((field: string) => {
            formData.append(field, fields[field])
          })
          formData.append('file', filePathObjects[key])
          return postToSignedUrl(presignedUrl, formData)
        }
      )
      await Promise.all(postToS3Promises)
        .then(async () => {
          const filesAddresses: string[] = []
          const assets = Object.keys(filePathObjects).map(name => {
            const fileAddress = `${flyoutS3Url}/${name}`
            filesAddresses.push(fileAddress)
            return {
              type: FileUploadType.EULA_FILES,
              metaData: productId,
              name,
              fileAddress,
            }
          })
          const dataToBeSent = {
            assets,
          }
          //This endpoint to be developed
          // await postAssets(partnerId, dataToBeSent)
          if (fileUploadCallback) {
            fileUploadCallback(filesAddresses)
          } else {
            if (folder === 'free_trial_eula') {
              await dispatch(addEulaFiles(productId, filesAddresses))
              await dispatch(updateOnReview(productId, true))
            } else if (folder === 'private_offer_eula') {
              await dispatch(
                addEulaFilesOnPrivateOffer(productId, filesAddresses)
              )
              await dispatch(
                sendPrivateOffer({
                  partnerId,
                  productId,
                  saveAsDraft,
                  crmOpportunityId,
                  crmObjectLink,
                }) as unknown as AnyAction
              )
            } else if (
              folder === 'reseller_end_customer_eula' ||
              folder === 'reseller_agreement_license'
            ) {
              const keyName =
                folder === 'reseller_end_customer_eula'
                  ? 'endCustomerEulaFiles'
                  : 'resellerLicenseAgreementFiles'
              await dispatch(
                addFilesOnResaleAuthorization(
                  productId,
                  filesAddresses,
                  keyName
                )
              )
            }
          }
        })
        .catch(error => {
          const globalState = getState()
          errorLogger({ globalState })(error as Error)
          throw error
        })
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([UploadToS3Unsuccessful])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }
